if (!customElements.get('lsg-collection-tabs')) {
  class LsgCollectionTabs extends HTMLElement {
    constructor() {
      super();
      this.initializedSwipers = new Map();
      this.selectedPetType = 'cats';
      this.initAttempts = 0;
      this.isTransitioning = false;
    }

connectedCallback() {
  if (typeof IntersectionObserver === 'undefined') {
    this.waitForSwiper(() => {
      requestAnimationFrame(() => {
        setTimeout(() => this.init(), 100);
      });
    });
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        observer.disconnect();
        this.waitForSwiper(() => {
          requestAnimationFrame(() => {
            setTimeout(() => this.init(), 100);
          });
        });
      }
    });
  }, { rootMargin: '400px 0px' });

  observer.observe(this);
}

    waitForSwiper(callback, maxAttempts = 50) {
      if (typeof Swiper !== 'undefined') {
        callback();
        return;
      }

      if (maxAttempts <= 0) return;

      setTimeout(() => {
        this.waitForSwiper(callback, maxAttempts - 1);
      }, 100);
    }

    init() {
      this.findElements();

      if (this.tabs.length === 0) {
        setTimeout(() => {
          this.findElements();
          if (this.tabs.length > 0) {
            this.setupEventListeners();
            const initialPetType = this.getInitialPetType();
            this.filterByPetType(initialPetType, true);
          }
        }, 200);
        return;
      }

      this.setupEventListeners();
      const initialPetType = this.getInitialPetType();
      this.filterByPetType(initialPetType, true);
    }

    findElements() {
      this.petTypeButtons = Array.from(this.querySelectorAll('.pet-type-button'));
      this.tabs = Array.from(this.querySelectorAll('.tab-button'));
      this.contents = Array.from(this.querySelectorAll('.tab-content'));
    }

    getInitialPetType() {
      const activeButton = this.petTypeButtons.find(button =>
        button.classList.contains('active-tab')
      );

      if (activeButton) {
        const petType = activeButton.getAttribute('data-pet-type');
        if (petType) {
          this.selectedPetType = petType;
          return petType;
        }
      }

      if (this.petTypeButtons.length > 0) {
        const firstButton = this.petTypeButtons[0];
        const petType = firstButton.getAttribute('data-pet-type');
        if (petType) {
          this.selectedPetType = petType;
          return petType;
        }
      }

      return 'cats';
    }

    setupEventListeners() {
      this.petTypeButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const petType = button.getAttribute('data-pet-type');

          this.petTypeButtons.forEach((b) => b.classList.remove('active-tab'));
          button.classList.add('active-tab');

          this.handlePetTypeClick(petType);
        });
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          this.handleCollectionTabClick(tab);
        });

        // Prefetch on hover (desktop) for snappier perceived performance
        tab.addEventListener('mouseenter', () => {
          const target = tab.getAttribute('data-target');
          const content = this.contents.find(c => c.getAttribute('data-tab') === target);
          if (content && content.dataset.loaded === 'false') {
            this.loadTabContent(content);
          }
        }, { once: true });
      });
    }

    handlePetTypeClick(petType) {
      this.selectedPetType = petType;
      this.filterByPetType(petType, false);
    }

    filterByPetType(petType, isInitialLoad = false) {
      this.contents.forEach((content) => {
        content.classList.add('hidden');
        this.destroySwiperForContainer(content);
      });

      this.tabs.forEach((t) => {
        t.classList.remove('active-tab');
      });

      const visibleTabs = [];

      this.tabs.forEach((tab) => {
        const tabPetType = tab.getAttribute('data-pet-type');

        if (tabPetType === petType) {
          tab.style.display = '';
          visibleTabs.push(tab);
        } else {
          tab.style.display = 'none';
          tab.classList.remove('active-tab');
        }
      });

      if (visibleTabs.length === 0) return;

      const tabsContainer = this.querySelector('.tabs-container');
      if (tabsContainer) {
        if (visibleTabs.length < 7) {
          tabsContainer.classList.remove('justify-start');
          tabsContainer.classList.add('md:justify-center');
        } else {
          tabsContainer.classList.remove('md:justify-center');
          tabsContainer.classList.add('justify-start');
        }
      }

      const firstVisibleTab = visibleTabs[0];
      firstVisibleTab.classList.add('active-tab');

      const target = firstVisibleTab.getAttribute('data-target');

      this.showContent(target, isInitialLoad);
    }

    async showContent(target, isInitialLoad = false) {
      const content = this.contents.find(
        (c) => c.getAttribute('data-tab') === target
      );

      if (!content) return;

      this.contents.forEach((c) => {
        if (c !== content) {
          c.classList.add('hidden');
          this.destroySwiperForContainer(c);
        }
      });

      // Lazy-load tab content if not yet loaded
      if (content.dataset.loaded === 'false') {
        await this.loadTabContent(content);
      }

      content.classList.remove('hidden');

      const slides = content.querySelectorAll('.swiper-slide');

      slides.forEach((slide) => {
        slide.style.display = '';
      });

      if (slides.length > 0) {
        const delay = isInitialLoad ? 100 : 120;

        setTimeout(() => {
          this.initSwiper(content, true);
        }, delay);
      }
    }

    async loadTabContent(content) {
      // Guard against duplicate loads
      if (content.dataset.loaded === 'true') return;
      if (content.dataset.loading === 'true') {
        // Wait for the in-flight load to finish
        return new Promise((resolve) => {
          const check = setInterval(() => {
            if (content.dataset.loading !== 'true') {
              clearInterval(check);
              resolve();
            }
          }, 50);
        });
      }

      content.dataset.loading = 'true';

      const collectionHandle = content.dataset.collectionHandle;
      const buttonType = content.dataset.buttonType || 'shop_now';
      const swiperWrapper = content.querySelector('.swiper-wrapper');
      const placeholder = swiperWrapper && swiperWrapper.querySelector('.tab-loading-placeholder');

      if (!collectionHandle || !swiperWrapper) {
        content.dataset.loading = 'false';
        return;
      }

      try {
        const url = `/collections/${collectionHandle}?view=card-only&button_type=${encodeURIComponent(buttonType)}`;
        const response = await fetch(url, {
          headers: { 'Accept': 'text/html' }
        });

        if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const slides = doc.querySelectorAll('.swiper-slide');

        if (slides.length === 0) throw new Error('No slides returned');

        if (placeholder) placeholder.remove();

        slides.forEach(slide => {
          swiperWrapper.appendChild(slide);
        });

        content.dataset.loaded = 'true';
        content.dataset.loading = 'false';
      } catch (err) {
        console.error('[lsg-collection-tabs] Failed to load tab content:', err);
        if (placeholder) {
          placeholder.textContent = 'Could not load products. Please refresh.';
        }
        content.dataset.loading = 'false';
      }
    }

    destroySwiperForContainer(container) {
      const swiperEl = container.querySelector('.swiper');
      if (!swiperEl) return;

      if (this.initializedSwipers.has(swiperEl)) {
        const swiperInstance = this.initializedSwipers.get(swiperEl);

        if (swiperInstance && !swiperInstance.destroyed) {
          swiperInstance.destroy(true, true);
        }

        this.initializedSwipers.delete(swiperEl);
      }

      if (swiperEl.swiper) {
        swiperEl.swiper.destroy(true, true);
      }
    }

    initSwiper(container, retry = false) {
      if (container.classList.contains('hidden')) return;

      const swiperEl = container.querySelector('.swiper');
      if (!swiperEl) return;

      // Don't init Swiper on placeholder-only content
      const realSlides = swiperEl.querySelectorAll('.swiper-slide:not(.tab-loading-placeholder)');
      if (realSlides.length === 0) return;

      this.destroySwiperForContainer(container);

      if (typeof Swiper === 'undefined') {
        if (retry && this.initAttempts < 5) {
          this.initAttempts++;

          setTimeout(() => {
            if (typeof Swiper !== 'undefined') {
              this.initSwiper(container, false);
            }
          }, 200);
        }
        return;
      }

      const paginationEl = swiperEl.querySelector('.swiper-pagination');
      const prevEl = swiperEl.querySelector('.swiper-button-prev');
      const nextEl = swiperEl.querySelector('.swiper-button-next');

      const swiperConfig = {
        speed: 350,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        spaceBetween: 0,

        breakpoints: {
          0: { slidesPerView: 'auto' },
          768: { slidesPerView: 'auto' },
          1024: { slidesPerView: 'auto' }
        }
      };

      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          type: 'progressbar'
        };
      }

      if (prevEl && nextEl) {
        swiperConfig.navigation = {
          prevEl: prevEl,
          nextEl: nextEl
        };
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const swiperInstance = new Swiper(swiperEl, swiperConfig);

          this.initializedSwipers.set(swiperEl, swiperInstance);
          this.initAttempts = 0;

          swiperInstance.update();
          swiperInstance.updateSize();
          swiperInstance.updateSlides();
        });
      });
    }

    handleCollectionTabClick(tab) {
      const target = tab.getAttribute('data-target');
      if (!target) return;

      const tabPetType = tab.getAttribute('data-pet-type');

      this.tabs.forEach((t) => {
        const tPetType = t.getAttribute('data-pet-type');
        if (tPetType === tabPetType && t.style.display !== 'none') {
          t.classList.remove('active-tab');
        }
      });

      tab.classList.add('active-tab');

      this.showContent(target);
    }
  }

  customElements.define('lsg-collection-tabs', LsgCollectionTabs);
}